/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Card,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Avatar,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import SendIcon from '@mui/icons-material/Send';
import PeopleIcon from '@mui/icons-material/People';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';

import { useQueryGetAllUsersList, useMutationSendBulkNotifications } from '../../hooks/react-query/useData';
import { getUserDetails } from '../../utils';

const SendNotifications = () => {
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = useForm({});

  const userDetails = getUserDetails();
  const navigate = useNavigate();

  // Get all users without pagination (separated by role)
  const { data: allUsersData, isFetching: isLoading } = useQueryGetAllUsersList(
    { token: userDetails?.token, role: userDetails?.role?.toLowerCase() },
    navigate,
    toast
  );

  // Combine all users from different roles into one array
  const getAllUsers = () => {
    if (!allUsersData?.data?.data) return [];
    const teachers = allUsersData.data?.data?.teachers || [];
    const edifiers = allUsersData.data?.data?.edifiers || [];
    const siteLeaders = allUsersData.data?.data?.siteLeaders || allUsersData.data?.data?.['site-leaders'] || [];
    return [...teachers, ...edifiers, ...siteLeaders];
  };

  const [bulkMessagesPayload, setBulkMessagesPayload] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState({
    edifiers: false,
    teachers: false,
    siteLeaders: false,
    all: false,
  });

  const { isLoading: isSendingNotifications, mutate: sendMessages } = useMutationSendBulkNotifications(
    bulkMessagesPayload,
    toast,
    userDetails?.token,
    setBulkMessagesPayload,
    setSelectedUsers,
    setValue,
    setSelectedRoles
  );

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    
    // If "Everyone" is checked, uncheck others and vice versa
    if (name === 'all') {
      setSelectedRoles({
        edifiers: false,
        teachers: false,
        siteLeaders: false,
        all: checked,
      });
    } else {
      // If any other checkbox is checked, uncheck "Everyone"
      setSelectedRoles((prevRoles) => ({
        ...prevRoles,
        [name]: checked,
        all: false,
      }));
    }
  };

  const onSubmit = (data) => {
    const userIds = [];

    selectedUsers?.forEach((user) => {
      userIds.push(user?._id);
    });

    if (userIds.length === 0) {
      toast.error('Please select at least one user or role');
      return;
    }

    setBulkMessagesPayload({ message: data?.message, users: userIds });
  };

  useEffect(() => {
    if (Object.keys(bulkMessagesPayload).length > 0) {
      sendMessages();
    }
  }, [bulkMessagesPayload]);

  // Filter users based on selected roles
  useEffect(() => {
    const allUsers = getAllUsers();
    if (allUsers.length > 0) {
      const hasAnyRoleSelected = selectedRoles.all || selectedRoles.edifiers || selectedRoles.teachers || selectedRoles.siteLeaders;
      
      if (!hasAnyRoleSelected) {
        // If no roles are selected, keep manual selections from autocomplete
        return;
      }

      let filteredUsers = [];

      if (selectedRoles.all) {
        filteredUsers = [...allUsers];
        setSelectedUsers(filteredUsers);
        return;
      }

      if (selectedRoles.edifiers) {
        filteredUsers = [...filteredUsers, ...allUsers.filter((user) => user.role === 'COACH')];
      }
      if (selectedRoles.teachers) {
        filteredUsers = [...filteredUsers, ...allUsers.filter((user) => user.role === 'TEACHER')];
      }
      if (selectedRoles.siteLeaders) {
        filteredUsers = [
          ...filteredUsers,
          ...allUsers.filter(
            (user) =>
              user.role === 'SITE-LEADER' ||
              (user.totalRoles && Array.isArray(user.totalRoles) && user.totalRoles.includes('SITE-LEADER'))
          ),
        ];
      }

      // Remove duplicates based on user ID and filter out invalid users
      const seenIds = new Set();
      const validUsers = filteredUsers.filter((user) => {
        // Filter out null/undefined users
        if (!user) return false;
        
        // Filter out users without valid ID
        const userId = user._id || user.id;
        if (!userId) return false;
        
        const userIdStr = String(userId);
        
        // Check if we've already seen this user ID
        if (seenIds.has(userIdStr)) {
          return false; // Duplicate, filter it out
        }
        
        seenIds.add(userIdStr);
        return true; // Valid, unique user
      });
      
      setSelectedUsers(validUsers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoles, allUsersData]);

  const getRoleCount = (role) => {
    if (!allUsersData?.data?.data) return 0;
    if (role === 'COACH') {
      return (allUsersData.data.data.edifiers || []).length;
    }
    if (role === 'TEACHER') {
      return (allUsersData.data.data.teachers || []).length;
    }
    if (role === 'SITE-LEADER') {
      return (allUsersData.data.data.siteLeaders || allUsersData.data.data['site-leaders'] || []).length;
    }
    return 0;
  };

  const getTotalUsersCount = () => {
    return getAllUsers().length;
  };

  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | Send Notifications</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ p: '0 !important' }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>

            <Typography variant="h3" sx={{ color: 'text.titles', mb: 0 }}>
              Send Notifications
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.95rem',
              maxWidth: '600px',
              lineHeight: 1.6,
            }}
          >
            Send bulk notifications to users by selecting specific roles or individual users. Messages will be delivered to all selected recipients.
          </Typography>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={48} />
          </Box>
        )}

        {!isLoading && (
          <Grid container spacing={3}>
            {/* Main Form Card */}
            <Grid item xs={12} lg={8}>
              <Card
                sx={{
                  width: '100%',
                  maxWidth: '965px',
                  borderRadius: '20px',
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.customShadows.card,
                  p: { xs: 3, sm: 4 },
                  height: 'fit-content',
                }}
              >
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <SendIcon sx={{ fontSize: 20 }} />
                      Compose Message
                    </Typography>
                    <TextField
                      label="Type your message here..."
                      value={watch('message') || ''}
                      variant="outlined"
                      multiline
                      rows={6}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                      {...register('message', {
                        required: 'Please type your message',
                        minLength: {
                          value: 10,
                          message: 'Message must be at least 10 characters long',
                        },
                      })}
                      error={errors && !!errors?.message}
                      helperText={errors?.message?.message}
                    />
                    <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary', display: 'block' }}>
                      {watch('message')?.length || 0} characters
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: 20 }} />
                      Select Recipients
                    </Typography>

                    <Autocomplete
                      multiple
                      limitTags={3}
                      options={getAllUsers()}
                      getOptionLabel={(option) => {
                        if (!option || typeof option !== 'object') return '';
                        const fname = option.fname || '';
                        const lname = option.lname || '';
                        const role = option.role === 'COACH' ? 'Edifier' : (option.role || '');
                        return `${fname} ${lname}${role ? ` (${role})` : ''}`.trim();
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return false;
                        const optionId = option._id || option.id;
                        const valueId = value._id || value.id;
                        if (optionId && valueId) {
                          return String(optionId) === String(valueId);
                        }
                        // Fallback comparison
                        return (
                          option.fname === value.fname &&
                          option.lname === value.lname &&
                          option.email === value.email
                        );
                      }}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue || inputValue.trim() === '') {
                          return options;
                        }
                        const query = inputValue.toLowerCase().trim();
                        return options.filter((user) => {
                          const fullName = `${user.fname || ''} ${user.lname || ''}`.toLowerCase();
                          const email = (user.email || '').toLowerCase();
                          const role = (user.role === 'COACH' ? 'Edifier' : user.role || '').toLowerCase();
                          return fullName.includes(query) || email.includes(query) || role.includes(query);
                        });
                      }}
                      onChange={(event, value) => {
                        setSelectedUsers(value || []);
                      }}
                      value={selectedUsers || []}
                      loading={isLoading}
                      renderOption={(props, option) => {
                        const key = option._id || option.id || `${option.fname || ''}_${option.lname || ''}_${option.email || ''}`;
                        return (
                          <Box
                            component="li"
                            {...props}
                            key={key}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              py: 1,
                              px: 2,
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                          <Avatar
                            src={option.image ? `${option.image}?w=40&h=40&fit=crop&auto=format&q=80` : null}
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: 'primary.main',
                              fontSize: '14px',
                              fontWeight: 600,
                              color: 'white',
                            }}
                            alt={`${option.fname || ''} ${option.lname || ''}`}
                          >
                            {option.fname?.[0] || ''}{option.lname?.[0] || ''}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                              {option.fname || ''} {option.lname || ''}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              {option.role === 'COACH' ? 'Edifier' : option.role || 'User'}
                            </Typography>
                          </Box>
                        </Box>
                      );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Select Individual Users"
                          placeholder="Search and select users..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                            },
                          }}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          {...register('users', {
                            validate: () => selectedUsers?.length > 0 || selectedRoles.all || selectedRoles.edifiers || selectedRoles.teachers || selectedRoles.siteLeaders || 'Please select users or roles',
                          })}
                          error={errors && !!errors?.users && selectedUsers.length === 0 && !selectedRoles.all && !selectedRoles.edifiers && !selectedRoles.teachers && !selectedRoles.siteLeaders}
                          helperText={errors?.users?.message}
                        />
                      )}
                      noOptionsText="No users found. Try a different search term."
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const key = option._id || option.id || `${option.fname || ''}_${option.lname || ''}_${option.email || ''}`;
                          return (
                          <Chip
                            {...getTagProps({ index })}
                            key={key}
                            avatar={
                              <Avatar
                                src={option.image ? `${option.image}?w=32&h=32&fit=crop&auto=format&q=80` : null}
                                sx={{
                                  width: 24,
                                  height: 24,
                                  bgcolor: 'primary.dark',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  color: 'white',
                                }}
                                alt={`${option.fname || ''} ${option.lname || ''}`}
                              >
                                {option.fname?.[0] || ''}{option.lname?.[0] || ''}
                              </Avatar>
                            }
                            label={`${option.fname || ''} ${option.lname || ''}`}
                            size="small"
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark' ? 'action.selected' : 'primary.lighter',
                              color: (theme) =>
                                theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
                              height: '32px',
                              '& .MuiChip-avatar': {
                                marginLeft: '4px',
                              },
                              '& .MuiChip-deleteIcon': {
                                color: 'inherit',
                              },
                            }}
                          />
                          );
                        })
                      }
                    />

                    {selectedUsers.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                          {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: 'text.primary',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <GroupIcon sx={{ fontSize: 20 }} />
                      Or Select by Role
                    </Typography>

                    <FormGroup>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Paper
                            elevation={selectedRoles.all ? 4 : 0}
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              border: (theme) =>
                                `2px solid ${selectedRoles.all ? theme.palette.primary.main : theme.palette.divider}`,
                              backgroundColor: (theme) =>
                                selectedRoles.all
                                  ? theme.palette.mode === 'dark'
                                    ? theme.palette.action.selected
                                    : theme.palette.primary.lighter
                                  : theme.palette.mode === 'dark'
                                    ? theme.palette.background.neutral
                                    : theme.palette.grey[50],
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: (theme) =>
                                  selectedRoles.all
                                    ? theme.palette.mode === 'dark'
                                      ? theme.palette.action.selected
                                      : theme.palette.primary.lighter
                                    : theme.palette.action.hover,
                              },
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRoles.all}
                                  onChange={handleCheckboxChange}
                                  name="all"
                                  sx={{
                                    '& .MuiSvgIcon-root': { color: 'primary.main' },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Everyone
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                    {getTotalUsersCount()} total users
                                  </Typography>
                                </Box>
                              }
                            />
                          </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Paper
                            elevation={selectedRoles.edifiers ? 4 : 0}
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              border: (theme) =>
                                `2px solid ${selectedRoles.edifiers ? theme.palette.primary.main : theme.palette.divider}`,
                              backgroundColor: (theme) =>
                                selectedRoles.edifiers
                                  ? theme.palette.mode === 'dark'
                                    ? theme.palette.action.selected
                                    : theme.palette.primary.lighter
                                  : theme.palette.mode === 'dark'
                                    ? theme.palette.background.neutral
                                    : theme.palette.grey[50],
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: (theme) =>
                                  selectedRoles.edifiers
                                    ? theme.palette.mode === 'dark'
                                      ? theme.palette.action.selected
                                      : theme.palette.primary.lighter
                                    : theme.palette.action.hover,
                              },
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRoles.edifiers}
                                  onChange={handleCheckboxChange}
                                  name="edifiers"
                                  sx={{
                                    '& .MuiSvgIcon-root': { color: 'primary.main' },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Edifiers
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                    {getRoleCount('COACH')} users
                                  </Typography>
                                </Box>
                              }
                            />
                          </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Paper
                            elevation={selectedRoles.teachers ? 4 : 0}
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              border: (theme) =>
                                `2px solid ${selectedRoles.teachers ? theme.palette.primary.main : theme.palette.divider}`,
                              backgroundColor: (theme) =>
                                selectedRoles.teachers
                                  ? theme.palette.mode === 'dark'
                                    ? theme.palette.action.selected
                                    : theme.palette.primary.lighter
                                  : theme.palette.mode === 'dark'
                                    ? theme.palette.background.neutral
                                    : theme.palette.grey[50],
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: (theme) =>
                                  selectedRoles.teachers
                                    ? theme.palette.mode === 'dark'
                                      ? theme.palette.action.selected
                                      : theme.palette.primary.lighter
                                    : theme.palette.action.hover,
                              },
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRoles.teachers}
                                  onChange={handleCheckboxChange}
                                  name="teachers"
                                  sx={{
                                    '& .MuiSvgIcon-root': { color: 'primary.main' },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Teachers
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                    {getRoleCount('TEACHER')} users
                                  </Typography>
                                </Box>
                              }
                            />
                          </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <Paper
                            elevation={selectedRoles.siteLeaders ? 4 : 0}
                            sx={{
                              p: 2,
                              borderRadius: '12px',
                              border: (theme) =>
                                `2px solid ${selectedRoles.siteLeaders ? theme.palette.primary.main : theme.palette.divider}`,
                              backgroundColor: (theme) =>
                                selectedRoles.siteLeaders
                                  ? theme.palette.mode === 'dark'
                                    ? theme.palette.action.selected
                                    : theme.palette.primary.lighter
                                  : theme.palette.mode === 'dark'
                                    ? theme.palette.background.neutral
                                    : theme.palette.grey[50],
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: (theme) =>
                                  selectedRoles.siteLeaders
                                    ? theme.palette.mode === 'dark'
                                      ? theme.palette.action.selected
                                      : theme.palette.primary.lighter
                                    : theme.palette.action.hover,
                              },
                            }}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRoles.siteLeaders}
                                  onChange={handleCheckboxChange}
                                  name="siteLeaders"
                                  sx={{
                                    '& .MuiSvgIcon-root': { color: 'primary.main' },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Site Leaders
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                    {getRoleCount('SITE-LEADER')} users
                                  </Typography>
                                </Box>
                              }
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    </FormGroup>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelectedUsers([]);
                        setSelectedRoles({
                          edifiers: false,
                          teachers: false,
                          siteLeaders: false,
                          all: false,
                        });
                        setValue('message', '');
                      }}
                      sx={{
                        borderRadius: '12px',
                        px: 3,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Clear All
                    </Button>
                    <LoadingButton
                      loading={isSendingNotifications}
                      type="submit"
                      variant="contained"
                      color="main"
                      endIcon={<SendIcon />}
                      sx={{
                        borderRadius: '12px',
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0px 4px 12px rgba(142, 113, 173, 0.3)',
                        '&:hover': {
                          boxShadow: '0px 6px 16px rgba(142, 113, 173, 0.4)',
                        },
                      }}
                    >
                      Send Notification
                    </LoadingButton>
                  </Box>
                </form>
              </Card>
            </Grid>

            {/* Summary Card */}
            <Grid item xs={12} lg={4}>
              <Card
                sx={{
                  width: '100%',
                  maxWidth: '965px',
                  borderRadius: '20px',
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => theme.customShadows.card,
                  p: { xs: 3, sm: 4 },
                  position: 'sticky',
                  top: 20,
                  height: 'fit-content',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 20 }} />
                  Recipient Summary
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark' ? 'background.neutral' : 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, fontWeight: 500 }}>
                      Total Selected
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {selectedUsers.filter((user) => user && (user._id || user.id)).length || 0}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {selectedUsers.filter((user) => user && (user._id || user.id)).length === 1 ? 'user' : 'users'} will receive this notification
                    </Typography>
                  </Box>

                  {(selectedRoles.all || selectedRoles.edifiers || selectedRoles.teachers || selectedRoles.siteLeaders) && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                        Selected Roles:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedRoles.all && (
                          <Chip
                            label="Everyone"
                            size="small"
                            sx={{
                              backgroundColor: 'primary.main',
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {selectedRoles.edifiers && (
                          <Chip
                            label={`Edifiers (${getRoleCount('COACH')})`}
                            size="small"
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark' ? 'action.selected' : 'primary.lighter',
                              color: (theme) =>
                                theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {selectedRoles.teachers && (
                          <Chip
                            label={`Teachers (${getRoleCount('TEACHER')})`}
                            size="small"
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark' ? 'action.selected' : 'primary.lighter',
                              color: (theme) =>
                                theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {selectedRoles.siteLeaders && (
                          <Chip
                            label={`Site Leaders (${getRoleCount('SITE-LEADER')})`}
                            size="small"
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.mode === 'dark' ? 'action.selected' : 'primary.lighter',
                              color: (theme) =>
                                theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}

                  {selectedUsers.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.primary' }}>
                        Selected Users:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {selectedUsers
                          .filter((user) => user && (user._id || user.id) && (user.fname || user.lname))
                          .slice(0, 10)
                          .map((user) => {
                            const key = user._id || user.id || `${user.fname || ''}_${user.lname || ''}_${user.email || ''}`;
                            return (
                              <Box
                                key={key}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1.5,
                                  p: 1.5,
                                  borderRadius: '8px',
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark' ? 'background.neutral' : 'grey.50',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                    borderColor: 'primary.light',
                                  },
                                }}
                              >
                                <Avatar
                                  src={user.image ? `${user.image}?w=40&h=40&fit=crop&auto=format&q=80` : undefined}
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                  }}
                                  alt={`${user.fname || ''} ${user.lname || ''}`}
                                >
                                  {user.fname?.[0] || ''}{user.lname?.[0] || ''}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                                    {user.fname || ''} {user.lname || ''}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                    {user.email}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        {selectedUsers.filter((user) => user && (user._id || user.id) && (user.fname || user.lname)).length > 10 && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', textAlign: 'center', mt: 0.5 }}>
                            +{selectedUsers.filter((user) => user && (user._id || user.id) && (user.fname || user.lname)).length - 10} more users
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default SendNotifications;


